import cn from 'classnames/bind';
import React, { FC, PropsWithChildren, useLayoutEffect, useState, useRef, useMemo } from 'react';

import {
    SeamlessResizeControllerProps,
    SeamlessResizeControllerContextValue,
} from './SeamlessResizeController.types';
import { SeamlessResizeControllerContext } from './SeamlessResizeControllerContext';
import { createKeyframeAnimation } from './helpers';
import styles from './SeamlessResizeController.module.css';

const cx = cn.bind(styles);

const SeamlessResizeController: FC<PropsWithChildren<SeamlessResizeControllerProps>> = (props) => {
    const { visible, containerRef, children } = props;
    const [animationTrigger, setAnimationTrigger] = useState(false);
    const [resizeToggler, setResizeToggler] = useState(false);
    const sizeRef = useRef<{ width: number; height: number } | undefined>();
    const prevSizeRef = useRef<{ width: number; height: number } | undefined>();

    useLayoutEffect(() => {
        if (containerRef.current && visible) {
            const container = containerRef.current;
            const containerRect = container.getBoundingClientRect();
            const width = Math.round(containerRect.width);
            const height = Math.round(containerRect.height);

            if (!sizeRef.current) {
                sizeRef.current = { width, height };
            } else {
                const { width: currentWidth, height: currentHeight } = sizeRef.current;
                const hasChanged = currentHeight !== height || currentWidth !== width;

                if (hasChanged) {
                    sizeRef.current = { width, height };
                    prevSizeRef.current = {
                        width: currentWidth,
                        height: currentHeight,
                    };

                    setAnimationTrigger(!animationTrigger);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resizeToggler]);

    useLayoutEffect(() => {
        if (!prevSizeRef.current || !sizeRef.current || !containerRef.current) {
            return;
        }

        const container = containerRef.current;
        const { width: currentWidth, height: currentHeight } = prevSizeRef.current;
        const { width, height } = sizeRef.current;

        const scaleX = currentWidth / width;
        const scaleY = currentHeight / height;

        const animation = createKeyframeAnimation({ x: scaleX, y: scaleY });

        let animationStyles = document.querySelector('.popup-animation');
        if (!animationStyles) {
            animationStyles = document.createElement('style');
            animationStyles.classList.add('popup-animation');
            animationStyles.textContent = animation;
            document.head.appendChild(animationStyles);
        } else {
            animationStyles.textContent = animation;
        }

        container.classList.remove(cx('animated'));
        // eslint-disable-next-line no-unused-expressions
        window.getComputedStyle(container).transform;
        container.classList.add(cx('animated'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [animationTrigger]);

    const contextValue: SeamlessResizeControllerContextValue = useMemo(
        () => ({
            toggleResize: () => setResizeToggler((prev) => !prev),
        }),
        []
    );

    return (
        <SeamlessResizeControllerContext.Provider value={contextValue}>
            {children}
        </SeamlessResizeControllerContext.Provider>
    );
};

export default SeamlessResizeController;
